import { useRef } from 'react';
import { useScroll, useTransform, motion } from 'framer-motion';
import animations from 'utils/animations';
import Seo from 'components/Seo';
import AnimationPage from 'components/AnimationPage';
import background from 'assets/images/about-reel-bg.png';
import { Link } from 'react-router-dom';
import LocationsList from 'components/Locations';
import "assets/styles/Company.css";

export default function About() {
  const { opacity } = animations.components;

  const container = useRef(null);

  const { scrollYProgress } = useScroll({
    target: container,                 
    offset: ['start end', 'end start']
  });

  const translateY = useTransform(scrollYProgress, [0, 1], ['20%', '-20%']);

  return (
    <AnimationPage>
      <Seo title="Company - Kollectiff" />
      <main>
        <div className="container company-about">
          <h1 className='sr-only'>About Kollectiff</h1>

          <motion.p {...opacity} transition={{ delay: 0.5 }} className='k-title-xxs md:k-title-xs lg:k-title-sm'>
            Kollectiff started in 2021, born out of the pandemic era. We believed brands needed to leverage emerging technologies to keep up with an ever evolving audience and their digital preferences.  We set out to help agencies respond to changing demands from clients, and help clients respond to changing customer preferences.
          </motion.p>

          <motion.p {...opacity} transition={{ delay: 0.7 }} className='k-title-xxs md:k-title-xs lg:k-title-sm'>
            Technology is our playground — we find ourselves constantly experimenting, prototyping, and pushing the edge of what’s possible. Our internal R&D fuels the work we do with clients, translating curiosity into execution and providing clarity on best practices in digital production frameworks.
          </motion.p>

          <motion.p {...opacity} transition={{ delay: 0.9 }} className='k-title-xxs md:k-title-xs lg:k-title-sm'>
            We operate as an independent global network of best-in-class engineers, producers, creatives, strategists, and designers—headquartered in Los Angeles, CA. With talent hubs in Buenos Aires, Mexico,  and Barcelona.  
          </motion.p>

          <motion.p {...opacity} transition={{ delay: 0.11 }} className='k-title-xxs md:k-title-xs lg:k-title-sm'>
            We partner with some of the world’s most respected agencies and Fortune 500 brands, collaborating from concept to code - to create experiences that are impossible to ignore, and brand platforms that are ready to scale.
          </motion.p>
        </div>

        <div className="company-philosophy" ref={container}>
          <div className="company-philosophy-content container">
            <motion.h2
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ delay: .2 }}
              className='k-title-sm md:k-title-md lg:k-title-lg'
            >
              Our Philosophy
            </motion.h2>
            <div className='company-philosophy-paragraphs'>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: .3 }}
                className='k-text-md md:k-text-lg lg:k-text-xl'
              >
                We believe creativity and technology should move together.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: .3 }}
                className='k-text-md md:k-text-lg lg:k-text-xl'
              >
                We believe bold ideas deserve thoughtful execution.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: .3 }}
                className='k-text-md md:k-text-lg lg:k-text-xl'
              >
                And that digital work should be as intuitive as it is innovative.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: .3 }}
                className='k-text-md md:k-text-lg lg:k-text-xl'
              >
                We don’t chase trends. We create work that shapes them.
              </motion.p>
            </div>
          </div>

          <motion.img
            src={background}
            alt=""
            className="object-cover w-full h-full"
            style={{ translateY }}
          />
        </div>

        <motion.div
          className="container-full company-work-section"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <div className="white-container company-work">
            <div className='company-work-content'>
              <motion.h2
                className="k-title-sm lg:k-title-md"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                How We Work
              </motion.h2>
              <motion.p
                className="k-text-md md:k-text-lg lg:k-text-xl"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                We partner with you from first idea to lasting impact.
              </motion.p>
            </div>
            <motion.div
                className="company-work-cards container"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
              <ul>
                <li className='company-work-card'>
                  <h3 className='k-title-xs'>Insights</h3>
                  <p className='k-text-md lg:k-text-lg'>We dive into your brief, business, and audience to uncover the creative and technical opportunities that truly matter.</p>
                </li>
                <li className='company-work-card'>
                  <h3 className='k-title-xs'>Prototyping</h3>
                  <p className='k-text-md lg:k-text-lg'>We turn concepts into testable experiences, rapidly prototyping to refine ideas and align before full-scale production.</p>
                </li>
                <li className='company-work-card'>
                  <h3 className='k-title-xs'>Implementation</h3>
                  <p className='k-text-md lg:k-text-lg'>We build with accuracy and care, from front-end design to back-end systems, delivering solutions ready to launch.</p>
                </li>
                <li className='company-work-card'>
                  <h3 className='k-title-xs'>Scaling</h3>
                  <p className='k-text-md lg:k-text-lg'>We optimize, expand, and adapt—rolling out across markets and integrating AI or data systems to ensure growth.</p>
                </li>
              </ul>
            </motion.div>
  
          </div>
        </motion.div>

        <div className="company">
          <LocationsList />

          <div className='container company-hiring'>
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ delay: .2 }}
              className="k-title-xs md:k-title-sm xl:k-title-md font-normal"
            >
              <Link to="/contact" className="underline">We’re hiring worldwide</Link> - We’re always happy to hear from talented people who are ready for what’s next, so drop us a line!
            </motion.h2>
          </div>
        </div>
      </main>
    </AnimationPage>
  );
}